import React, {
  HTMLInputTypeAttribute,
  InputHTMLAttributes,
  KeyboardEventHandler,
  ReactNode,
  forwardRef,
  useEffect,
  useState,
} from 'react';
import styled from 'styled-components';

import { ReactComponent as HidePasswordIcon } from 'app/assets/icons/hide-password-icon.svg';
import { ReactComponent as ShowPasswordIcon } from 'app/assets/icons/show-password-icon.svg';

import Typography from '../Typography';
import './style.css';
import clsx from 'clsx';

const Label = styled.div<{ disabled?: boolean }>`
  font-weight: 700;
  font-size: 14px;
  line-height: 23px;
  color: ${({ disabled }) => (disabled ? 'var(--neutral-6)' : 'var(--gray-700)')};
`;

const Input = styled.input<{ disabledInput?: boolean; isInvalid?: boolean }>`
  font-weight: 400;
  font-size: 16px;
  line-height: 26px;
  height: 43px;
  color: var(--neutral-10);
  box-shadow: ${({ isInvalid }) => (isInvalid ? '0 0 0 0.25rem rgba(241, 65, 108, 0.25)' : 'none')};
  ${({ disabledInput }) =>
    disabledInput
      ? `background-color:var(--neutral-2-5) !important;`
      : `color:var(--neutral-10) !important; background-color:var(--neutral-2) !important;`}
  padding-right: ${({ isInvalid }) => (isInvalid ? '78px !important' : '40px')};
`;

const PasswordIcon = styled.span<{ status: boolean }>`
  position: absolute;
  right: 10px;
  cursor: pointer;
  top: 10px;
  margin-right: ${({ status }) => (status ? '40px' : '')};
`;

const renderPasswordIcon = (passwordShown?: boolean) => {
  return passwordShown ? <ShowPasswordIcon /> : <HidePasswordIcon />;
};

interface Props {
  label?: ReactNode;
  rootClass?: string;
  formik: any;
  field: string;
  placeholder: string;
  isRequired?: boolean;
  isDisable?: boolean;
  isTouched?: boolean;
  typeInput?: HTMLInputTypeAttribute;
  isShowError?: boolean;
  touched?: boolean;
  messageError?: string;
  passwordShown?: boolean;
  setPasswordShown?: () => void;
  message?: string;
  marginBottom?: number;
  onKeyDown?: KeyboardEventHandler;
  valueShowIconInput?: string;
  isShowIcon?: boolean;
  defaultValue?: any;
  extraLabelClass?: string;
}

const FormInput = forwardRef<HTMLInputElement, Props & InputHTMLAttributes<HTMLInputElement>>(
  (
    {
      label,
      placeholder,
      rootClass,
      field,
      formik,
      isRequired = false,
      isDisable = false,
      isTouched = true,
      typeInput = 'text',
      isShowError = true,
      touched,
      messageError,
      passwordShown,
      message,
      setPasswordShown,
      marginBottom = 24,
      onKeyDown,
      valueShowIconInput,
      isShowIcon = true,
      defaultValue = '',
      extraLabelClass = '',
      ...rest
    },
    ref,
  ) => {
    const isError = touched ?? formik.touched[field];
    const newMessageError = messageError ?? formik.errors[field];
    const newValue = valueShowIconInput ?? formik.values[field];
    const [inputValue, setInputValue] = useState<string>(
      formik.getFieldProps(field).value || defaultValue,
    );

    useEffect(() => {
      setInputValue(formik.getFieldProps(field).value?.toString() ?? '');
    }, [formik.values[field]]);

    const renderMessage = () => {
      if (isShowError && isError && newMessageError) {
        return (
          <div className="fv-help-block">
            <span role="alert">{newMessageError}</span>
          </div>
        );
      }
      if (formik.values[field] && message) {
        return (
          <Typography variant="body-4" lineHeight={23} style={{ marginTop: '0.3rem' }}>
            {message}
          </Typography>
        );
      }
      return null;
    };

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      let value = e.target.value;
      if (typeInput === 'number') {
        value = value.replace(/^0*(?=\d)/, '');
      }
      setInputValue(value);
      formik.setFieldValue(field, value || defaultValue);
    };

    const onBlur = () => {
      if (isTouched) formik.setFieldTouched(field, true);
    };

    return (
      <div className={clsx('position-relative fv-row', rootClass)} style={{ marginBottom }}>
        {label && (
          <Label
            disabled={isDisable}
            className={clsx(isRequired ? 'required' : '', extraLabelClass)}
          >
            {label}
          </Label>
        )}
        <div className="d-flex align-items-center justify-content-end position-relative">
          <Input
            ref={ref}
            disabledInput={isDisable}
            placeholder={placeholder}
            value={inputValue}
            type={typeInput}
            name={field}
            className={clsx('form-control form-control-solid mb-3 mb-lg-0', {
              'is-invalid': isError && newMessageError && isShowIcon,
              'is-valid': isError && newValue && !isDisable && !newMessageError && isShowIcon,
            })}
            autoComplete="new-password"
            disabled={formik.isSubmitting || isDisable}
            onKeyDown={onKeyDown}
            onChange={onChange}
            onBlur={onBlur}
            isInvalid={isError && newMessageError && isShowIcon}
            {...rest}
          />
          {(typeInput === 'password' || passwordShown) && (
            <PasswordIcon status={!!(isError && isShowIcon)} onClick={setPasswordShown}>
              {renderPasswordIcon(passwordShown)}
            </PasswordIcon>
          )}
        </div>
        <div className="position-absolute fv-plugins-message-container" style={{ bottom: -22 }}>
          {renderMessage()}
        </div>
      </div>
    );
  },
);

FormInput.displayName = 'FormInput';
export { FormInput };
